import { loadProgress, saveProgress, createFreshProgress } from '../../shared/progress';
import { extractFromBrowserFile } from './ocr-client';
import { renderParentConfirm } from './parent-confirm';
import { promoteApprovedItems } from './promote';
import { encodeKPsAsQR } from './qr-sync';
import { pageShell, academicButton } from '../ui/components';
import { ensureAcademicTheme } from '../ui/theme';

/**
 * 上傳考卷(家長操作)— OCR → 逐題確認 → 寫入弱點 → QR 同步到 iPad。
 *
 * 視覺 = 學術風格設計系統(boss 2026-06-08;見 wiki page-470f59c0)。整頁套 `pageShell`
 * (serif「上傳考卷」標題列),檔案選擇用紙片式 field、回主畫面 / 完成等按鈕用
 * `academicButton`(主動作 primary、回主畫面 secondary)。
 *
 * 行為 / DOM 全保留:`.exam-upload`(root)、`#exam-file`(file input)、`#exam-status`、
 * `#exam-back`(回主畫面,remove 整頁 + onBack)、`.upload-summary` + 內 `<img>`(QR)、
 * `#upload-back-home`。測試依賴的節點不動。pageShell 不帶 onBack(沿用 review-hub 慣例,
 * 自管返回鈕,避免多掛一顆 ✕),維持原本「無固定離開鈕、靠頁內回主畫面」的行為。
 */
export function renderExamUpload(container: HTMLElement, onBack: () => void): void {
  ensureAcademicTheme();
  ensureExamUploadStyle();

  const shell = pageShell(container, {
    title: '上傳考卷',
    subtitle: '家長操作:選一張小孩做完的考卷,系統用 AI 自動識別題目,再請你逐題確認對錯。',
  });

  const root = document.createElement('div');
  root.className = 'exam-upload';
  root.setAttribute('data-testid', 'exam-upload-screen');
  shell.body.appendChild(root);

  // 檔案選擇紙片 + 狀態區。標題/說明已在 pageShell 標題列,這裡只留操作。
  const field = document.createElement('label');
  field.className = 'exam-upload__field';
  field.innerHTML = `
    <span class="exam-upload__field-label">選擇考卷照片</span>
    <input type="file" accept="image/*" id="exam-file" />
  `;
  root.appendChild(field);

  const status = document.createElement('div');
  status.id = 'exam-status';
  status.className = 'exam-upload__status';
  root.appendChild(status);

  const backBtn = academicButton({
    label: '回主畫面',
    variant: 'secondary',
    onClick: () => { shell.root.remove(); onBack(); },
  });
  backBtn.id = 'exam-back';
  backBtn.classList.add('exam-upload__back');
  root.appendChild(backBtn);

  const input = root.querySelector('#exam-file') as HTMLInputElement;

  input.addEventListener('change', async () => {
    const file = input.files?.[0];
    if (!file) return;
    status.innerHTML = '<p class="exam-upload__note">上傳中 + AI 分析(約 10–20 秒)…</p>';
    try {
      const items = await extractFromBrowserFile(file);
      status.innerHTML = `<p class="exam-upload__note">已識別到 ${items.length} 道題目,進入確認。</p>`;
      renderParentConfirm(root, items, async (inputs) => {
        const progress = loadProgress() ?? createFreshProgress('指揮官');
        const result = promoteApprovedItems(progress, inputs);
        saveProgress(progress);
        // Render QR sync UI
        const qrDataUrl = await encodeKPsAsQR(result.studentKPsDelta);
        const summary = document.createElement('div');
        summary.className = 'upload-summary ef-card';
        summary.innerHTML = `
          <h2 class="upload-summary__title">完成</h2>
          <p>已寫入 ${result.promoted} 道題目記錄(${result.discarded} 道丟棄)。</p>
          ${result.mistakesAdded > 0 ? `<p>其中 ${result.mistakesAdded} 道錯題已加入錯題本,小孩可在主畫面「錯題本」重新練習。</p>` : ''}
          <h3 class="upload-summary__sub">在 iPad 上同步</h3>
          <p>用 iPad 開啟遊戲後,進入 ⚙️ → 接收同步,掃下面的 QR code:</p>
          <img class="upload-summary__qr" src="${qrDataUrl}" alt="同步 QR" />
          <div class="upload-summary__actions"></div>
        `;
        root.appendChild(summary);
        const homeBtn = academicButton({
          label: '回主畫面',
          variant: 'primary',
          onClick: () => { shell.root.remove(); onBack(); },
        });
        homeBtn.id = 'upload-back-home';
        summary.querySelector('.upload-summary__actions')!.appendChild(homeBtn);
      });
    } catch (e) {
      status.innerHTML = `<p class="exam-upload__error">失敗:${(e as Error).message}</p>`;
    }
  });
}

/** 上傳考卷 scoped 樣式(只用 ef- token,不碰全域 styles.css)。冪等注入。 */
const EXAM_UPLOAD_STYLE_ID = 'ef-exam-upload-style';
function ensureExamUploadStyle(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(EXAM_UPLOAD_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = EXAM_UPLOAD_STYLE_ID;
  style.textContent = `
.exam-upload { display:flex; flex-direction:column; gap:16px; color:var(--ef-ink-soft); font-family:var(--ef-sans); }
.exam-upload__field {
  display:flex; flex-direction:column; gap:8px; padding:18px; background:var(--ef-paper-bright);
  border:1px dashed var(--ef-line); border-radius:var(--ef-radius); box-shadow:var(--ef-shadow-card);
}
.exam-upload__field-label { font-family:var(--ef-serif); font-weight:600; font-size:16px; color:var(--ef-ink); letter-spacing:0.5px; }
.exam-upload__field input[type="file"] { font-family:var(--ef-sans); font-size:14px; color:var(--ef-ink-soft); }
.exam-upload__status:empty { display:none; }
.exam-upload__note { margin:0; font-size:14px; color:var(--ef-sub); }
.exam-upload__error { margin:0; font-size:14px; color:#b3261e; }
.exam-upload__back { align-self:flex-start; }
.upload-summary { gap:8px; cursor:default; }
.upload-summary:hover { transform:none; box-shadow:var(--ef-shadow-card); border-color:var(--ef-line); }
.upload-summary__title { margin:0; font-family:var(--ef-serif); font-weight:700; font-size:20px; color:var(--ef-ink); }
.upload-summary__sub { margin:8px 0 0; font-family:var(--ef-serif); font-weight:600; font-size:16px; color:var(--ef-ink); }
.upload-summary p { margin:0; font-size:14px; line-height:1.6; color:var(--ef-ink-soft); }
.upload-summary__qr { display:block; width:160px; height:160px; margin:4px 0; border:1px solid var(--ef-line); border-radius:var(--ef-radius-sm); background:#fff; }
.upload-summary__actions { margin-top:4px; }
`;
  document.head.appendChild(style);
}
